import { useState, useEffect, useRef, Fragment, ChangeEvent } from 'react';
import { useUser } from 'app/state/contexts/UserContext';
import { api } from 'shared/api/client';
import { Spinner } from 'app/components';
import Prism from 'prismjs';
import { v4 as uuidv4 } from 'uuid';
import 'prismjs/themes/prism.css';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useStyles } from 'entities/style';
import { useGetDocument, DesignEntry, SectionEntry, DesignStyle, EditorDocument } from 'entities/document';
import { i18n } from 'shared/i18n';
import { useDocumentTemplates } from 'entities/template';
import { useGetSectionPages } from 'entities/document';

type StyleResource = DesignStyle & { id?: string };

type TemplateSummary = { superId: string; name: string };
type PageInfo = { name?: string; internalIdx: number };

const actions = {
  DOCUMENT_ACTION: 'update_document_style',
  TEMPLATE_ACTION: 'update_template_style',
};

const getActionForStyle = (document: EditorDocument, styleId?: string) => {
  let type = 'document';

  if (document?.content?.design) {
    const style = document.content.design.find((s: DesignEntry) => s.styleId === styleId);

    if (style) {
      type = style.type;
    }
  }
  switch (type) {
    case 'template':
      return actions.TEMPLATE_ACTION;
    default:
      return actions.DOCUMENT_ACTION;
  }
};

interface CodeBlockProps {
  style?: StyleResource;
  onSave?: ((style: StyleResource, content: string) => void) | null;
  onDelete?: (() => void) | null;
}

export const CodeBlock = ({ style = {}, onSave = null, onDelete }: CodeBlockProps) => {
  const codeRef = useRef<HTMLElement>(null);

  // This component only ever renders string style content (raw SCSS/CSS).
  const styleContent = style.content as string | undefined;

  const [editMode, setEditMode] = useState(false);
  const [height, setHeight] = useState(0);
  const [content, setContent] = useState(styleContent);

  useEffect(() => {
    setContent(styleContent);
    setEditMode(false);
  }, [styleContent]);

  useEffect(() => {
    if (!editMode && codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [editMode, styleContent]);

  const handleClickEdit = () => {
    // Get the height of the box so we can match the textareas height.
    setHeight(codeRef.current?.getBoundingClientRect().height ?? 0);
    setEditMode(true);
  };

  const handleSave = () => {
    onSave?.(style, content ?? '');
  };

  const handleOnChangeContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  return (
    <div className="card mb-3">
      <div className="card-body position-relative">
        {editMode ? (
          <>
            <textarea style={{ height: `${height}px` }} className="bg-light w-100 border-0 resize-none" value={content} onChange={handleOnChangeContent} />
            <button className="btn btn-secondary btn-sm position-absolute top-1 end-1" onClick={handleSave}>
              <i className="fa-regular fa-floppy-disk" />
            </button>
          </>
        ) : (
          <>
            <pre className="bg-light">
              <code className="language-css" ref={codeRef}>
                {styleContent}
              </code>
            </pre>
            <button className="btn btn-secondary btn-sm position-absolute top-1 end-1" onClick={handleClickEdit}>
              <i className="fa-regular fa-pen" />
            </button>
            {onDelete && (
              <button className="btn btn-secondary btn-sm position-absolute top-1 end-4" onClick={onDelete}>
                <i className="fa-regular fa-trash" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ProjectCSS = ({ documentId = null }: { documentId?: number | string | null }) => {
  const { selectedOrganization } = useUser();
  const documentIdValue = documentId as number | string;
  const { data, isLoading: isDocumentLoading, refetch: refetchDocument } = useGetDocument(documentIdValue, selectedOrganization);
  const pDocument = data;
  const styleIds = pDocument?.content.design?.map((d: DesignEntry) => d.styleId) ?? [];
  const { data: stylesData, isLoading: isStylesLoading } = useStyles({
    organization: selectedOrganization,
    documentRevisionId: pDocument?.revisionId,
    styleIds,
    parsed: false,
  });
  const styles = (stylesData ?? []) as StyleResource[];
  const { data: documentTemplates, isLoading: isDocumentTemplatesLoading } = useDocumentTemplates(documentIdValue);
  const sectionRevisionIds = (pDocument?.content.sections ?? []).map((s: SectionEntry) => s.revisionId);
  const { data: sectionPages, isLoading: isSectionPagesLoading } = useGetSectionPages(pDocument?.revisionId, sectionRevisionIds);
  const pageInfoById = (sectionPages ?? []).flat().reduce<Record<string, PageInfo>>((acc, p) => {
    if (p.pageRevisionId) acc[p.pageRevisionId] = p;
    return acc;
  }, {});

  const handleSaveStyle = async (oldStyle: StyleResource = {}, newContent = '') => {
    const newStyle = {
      ...oldStyle,
      id: uuidv4(),
      documentSuperId: Number(documentId),
      content: newContent,
    };

    if (!pDocument) return;
    const type = pDocument.content.design?.find((d: DesignEntry) => d.styleId === oldStyle.id)?.type || 'document';
    api
      .POST('/styles/', { body: newStyle })
      .then((res) => {
        return api
          .POST('/documents/{superId}/actions', {
            params: { path: { superId: Number(documentId) }, query: { compileSass: 'true' } },
            body: {
              action: getActionForStyle(pDocument, oldStyle.id),
              revisionId: oldStyle.id,
              data: {
                styleId: newStyle.id,
                type,
              },
            },
          })
          .then(() => {
            refetchDocument();
            return res;
          });
      })
      .catch(ServerErrorHandler);
  };
  if (isStylesLoading || isDocumentLoading || isDocumentTemplatesLoading || isSectionPagesLoading || !pDocument) {
    return <Spinner />;
  }
  const typeOrder = ['graphical-variables', 'paper', 'document', 'form-classes', 'template', 'page-variables'];
  const sortedStyles = [...styles].sort((a, b) => {
    const designA = pDocument.content.design.find((d: DesignEntry) => d.styleId === a.id);
    const designB = pDocument.content.design.find((d: DesignEntry) => d.styleId === b.id);
    const orderA = designA ? typeOrder.indexOf(designA.type) : typeOrder.length;
    const orderB = designB ? typeOrder.indexOf(designB.type) : typeOrder.length;
    return (orderA === -1 ? typeOrder.length : orderA) - (orderB === -1 ? typeOrder.length : orderB);
  });

  return (
    <>
      {sortedStyles.map((style) => {
        const design = pDocument.content.design.find((d: DesignEntry) => d.styleId === style.id);
        if (!design) return null;
        return (
          <Fragment key={style.id}>
            <div className="fw-bold mt-4 mb-2">{getDetails(design, documentTemplates, pageInfoById)}</div>
            <CodeBlock style={style} onSave={handleSaveStyle} />
          </Fragment>
        );
      })}
    </>
  );
};

const getDetails = (design: DesignEntry, templates?: TemplateSummary[], pageInfoById: Record<string, PageInfo> = {}) => {
  switch (design.type) {
    case 'document':
      return `${design.type} (${design.templateSuperId}) `;
    case 'template':
      const template = templates?.find((t) => t.superId === design.templateSuperId);
      return `${design.type} - ${template ? `${template.name}` : i18n('project-details:project-css.no-template')} (.xrp-template-${design.templateSuperId})`;
    case 'graphical-variables':
      return design.type;
    case 'page-variables': {
      const info = design.pageId ? pageInfoById[design.pageId] : undefined;
      if (info) {
        return `${design.type} \n ${info.name} - p.${info.internalIdx + 1} (#id-${design.pageId})`;
      }
      return `${design.type} (#id-${design.pageId})`;
    }
    case 'form-classes':
      return 'form-classes';
    default:
      return design.type;
  }
};

export default ProjectCSS;
